import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * The approved WhatsApp templates the out-of-window composer may offer.
 *
 * Mirrors WhatsAppTemplateSummary in
 * supabase/functions/_shared/whatsapp-templates.ts. This repo mirrors rather
 * than importing across src/ ↔ supabase/functions/ (see
 * src/lib/broadcast-audience.ts for the same arrangement); the shape is small
 * and flat, and the edge function's own tests pin what it emits.
 */
export type WhatsAppTemplate = {
  name: string;
  /** The template's own language — never assume "de". */
  language: string;
  category: string;
  variableCount: number;
  /** BODY text with `{{n}}` placeholders intact, for the preview. */
  bodyText: string;
};

/**
 * Distinguishes "Meta has no approved templates" from "we could not ask".
 *
 * The composer must not tell an admin to go create a template in Meta when
 * the truth is that Graph was unreachable — and on `unavailable` it falls
 * back to the hardcoded nudge rather than removing the only way to reach a
 * tenant. list-whatsapp-templates answers 503 (not configured) and 502
 * (could not reach WhatsApp) for exactly this distinction.
 */
export type WhatsAppTemplatesResult =
  | { status: "ok"; templates: WhatsAppTemplate[] }
  | { status: "unavailable" };

export function useWhatsAppTemplates(enabled: boolean) {
  return useQuery<WhatsAppTemplatesResult>({
    queryKey: ["whatsapp-templates"],
    // Templates change when someone edits them in Meta's console — rare, and
    // never as a result of anything happening in this app. A fetch per render
    // would be a Graph call per keystroke-adjacent re-render.
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
    queryFn: async (): Promise<WhatsAppTemplatesResult> => {
      const { data, error } = await supabase.functions.invoke<{
        templates?: WhatsAppTemplate[];
      }>("list-whatsapp-templates", { method: "GET" });

      // Every failure is the same to the caller: we cannot offer a list.
      // Deliberately NOT thrown — a throw would put react-query in an error
      // state the composer would have to special-case anyway, and the
      // fallback is the same either way.
      if (error || !data || !Array.isArray(data.templates)) {
        return { status: "unavailable" };
      }
      return { status: "ok", templates: data.templates };
    },
  });
}
